import { useState } from 'react';
import { PartnerValueSection } from '../components/listing/PartnerValueSection';
import { LeadForm } from '../components/listing/LeadForm';
import { OtpVerifyPanel } from '../components/listing/OtpVerifyPanel';
import { ThankYouPanel } from '../components/listing/ThankYouPanel';

/**
 * "List your property" — a listing submission, not PMS onboarding.
 *
 * Hosts the 3-state machine in local React state (not URL / localStorage):
 *   STATE A `form`    → LeadForm (submit → pending lead + OTP emailed)
 *   STATE B `verify`  → OtpVerifyPanel (enter the 6-digit code)
 *   STATE C `success` → ThankYouPanel
 *
 * A hard refresh remounts at STATE A with an empty form (stateless v1). The
 * pending lead + OTP live server-side; nothing routes into the PMS here.
 */
type Phase =
  | { name: 'form' }
  | { name: 'verify'; email: string; ownerName: string }
  | { name: 'success'; ownerName: string };

export default function ListYourPropertyPage() {
  const [phase, setPhase] = useState<Phase>({ name: 'form' });

  return (
    <>
      <PartnerValueSection />
      {/* The #lead-form anchor lives on the section so the hero CTA resolves in every phase. */}
      <section id="lead-form" className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        {phase.name === 'form' && (
          <>
            <div className="mb-8">
              <h2 className="font-serif text-3xl font-semibold text-ink">
                Tell us about your hotel
              </h2>
              <p className="mt-2 text-ink-soft">
                Share a few details and our team will review your hotel for BestAcco.
                We will be in touch about next steps.
              </p>
            </div>
            <LeadForm
              onListed={(email, ownerName) => setPhase({ name: 'verify', email, ownerName })}
            />
          </>
        )}

        {phase.name === 'verify' && (
          <OtpVerifyPanel
            email={phase.email}
            ownerName={phase.ownerName}
            onVerified={(ownerName) => setPhase({ name: 'success', ownerName })}
            onEditDetails={() => setPhase({ name: 'form' })}
          />
        )}

        {phase.name === 'success' && (
          <ThankYouPanel
            ownerName={phase.ownerName}
            onSubmitAnother={() => setPhase({ name: 'form' })}
          />
        )}
      </section>
    </>
  );
}
